/**
 * PDFをページごとの画像に分解する（すべてブラウザ内で完結）。
 *
 * 「紙から取り込み」の現実的な導線は、複合機で紙束を一括スキャンした
 * **PDF 1本** である。1ページずつ書き出させるのは「溜まった紙を楽に片付ける」
 * という機能の目的と噛み合わない。
 *
 * 設計上の判断:
 * - **サーバーには一切PDFを送らない**。ページ画像化までブラウザで行い、
 *   Edge Function には既存と同じ「画像1枚」を渡す。
 *   → OCR関数は無変更。PDF原本もサーバーに残らない（保持しない方針と整合）。
 * - pdfjs は動的importで読み込む。1MB超あるため、この機能を使わない
 *   ユーザーの初回ロードを重くしない。
 * - 描画解像度は手書きが読める範囲を優先（長辺2200px目安）。
 *   ocrService の maxWidthOrHeight と揃えているので、この後の圧縮で劣化しない。
 */
import { logger } from '../utils/logger';

/** 1ファイルあたりの上限ページ数。超過分は切り捨てるが、必ず呼び出し側へ通知する。 */
export const PDF_MAX_PAGES = 50;

/** 手書きを読ませるための目標長辺(px)。ocrService の圧縮設定と揃えている。 */
const TARGET_LONG_EDGE = 2200;

export interface PdfConvertProgress {
  /** 変換中のページ（1始まり） */
  page: number;
  /** 変換対象の総ページ数（上限適用後） */
  total: number;
}

export interface PdfConvertResult {
  /** ページ順のJPEG画像。ファイル名は「元ファイル名_p1.jpg」形式 */
  images: File[];
  /** PDFが持っていた実際のページ数 */
  totalPages: number;
  /** PDF_MAX_PAGES を超えて切り捨てたページ数（0なら全ページ変換済み） */
  skippedPages: number;
}

let workerConfigured = false;

/** pdfjs本体を遅延ロードし、ワーカーを一度だけ設定する。 */
async function loadPdfjs() {
  const pdfjs = await import('pdfjs-dist');
  if (!workerConfigured) {
    // ワーカーは同一オリジンからバンドル配信する（CDN依存を作らない）
    const worker = await import('pdfjs-dist/build/pdf.worker.min.mjs?url');
    pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
    workerConfigured = true;
  }
  return pdfjs;
}

/** PDFかどうか。ブラウザによって type が空になることがあるので拡張子も見る。 */
export function isPdfFile(file: File): boolean {
  return file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
}

/**
 * PDFを1ページ1枚のJPEGに変換する。
 * パスワード付き・破損PDFは Error を投げるので、呼び出し側でメッセージを出す。
 */
export async function pdfToPageImages(
  file: File,
  onProgress?: (p: PdfConvertProgress) => void
): Promise<PdfConvertResult> {
  const pdfjs = await loadPdfjs();
  const buffer = await file.arrayBuffer();

  // 破棄は loadingTask 側で行う（PDFDocumentProxy に destroy() は無い）
  const loadingTask = pdfjs.getDocument({ data: new Uint8Array(buffer) });
  let doc;
  try {
    doc = await loadingTask.promise;
  } catch (e) {
    const name = (e as { name?: string })?.name;
    if (name === 'PasswordException') {
      throw new Error('パスワード付きのPDFは取り込めません。保護を解除してからお試しください。');
    }
    logger.error('pdfToPageImages: getDocument failed', e);
    throw new Error('PDFを開けませんでした。ファイルが破損している可能性があります。');
  }

  const totalPages = doc.numPages;
  const target = Math.min(totalPages, PDF_MAX_PAGES);
  const baseName = file.name.replace(/\.pdf$/i, '');
  const images: File[] = [];

  try {
    for (let n = 1; n <= target; n++) {
      onProgress?.({ page: n, total: target });

      const page = await doc.getPage(n);
      // 等倍のサイズから、長辺が TARGET_LONG_EDGE になる倍率を求める
      const base = page.getViewport({ scale: 1 });
      const scale = TARGET_LONG_EDGE / Math.max(base.width, base.height);
      const viewport = page.getViewport({ scale });

      const canvas = document.createElement('canvas');
      canvas.width = Math.floor(viewport.width);
      canvas.height = Math.floor(viewport.height);
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('画像化に必要なCanvasを準備できませんでした。');

      // スキャンPDFは背景が透明なことがある。白で塗ってから描かないと黒地になる。
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      await page.render({ canvas, canvasContext: ctx, viewport }).promise;

      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, 'image/jpeg', 0.92)
      );
      // 明示的に解放（50ページ分のcanvasを抱えたままにしない）
      canvas.width = 0;
      canvas.height = 0;
      page.cleanup();

      if (!blob) {
        logger.warn(`pdfToPageImages: page ${n} produced no blob, skipping`);
        continue;
      }
      images.push(new File([blob], `${baseName}_p${n}.jpg`, { type: 'image/jpeg' }));
    }
  } finally {
    // ワーカーと転送層を解放する。50ページ分のメモリを抱えたままにしない。
    await loadingTask.destroy();
  }

  return { images, totalPages, skippedPages: Math.max(0, totalPages - target) };
}
